import { useCallback } from 'react';
import { format } from 'date-fns';
import { ChevronLeftIcon } from 'lucide-react';
import { toast } from 'sonner';

import { useCustomerBookingCtx } from '@/app/(app)/customers/[id]/customer-booking-provider';
import { useCustomerCtx } from '@/app/(app)/customers/[id]/provider';
import { DateTimeInUTC, TimeInUTC } from '@/components/atoms/datetime';
import { EmployeeAvatar } from '@/components/molecules/employee-avatar';
import { useApiV1Ctx } from '@/components/providers/v1-api';
import { useEmployeeById } from '@/components/providers/v1-office-hooks';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { formatMetersAsMiles, formatTravelTime } from '@/lib/format';
import { cn } from '@/lib/utils';

export function CustomerBookingConfirmation() {
  // Details of appointment is compiled from multiple places:
  //   search params, the selected availability, and the customer (service plans)
  //
  // TODO: this feels brittle, can break if e.g. search strategy changes,
  //   these details should be on the availability/slot object
  const { customer, refetch } = useCustomerCtx();
  const { api } = useApiV1Ctx();
  const { params, selected, setSelected, editingServiceEvent, clearBooking, note, setNote } = useCustomerBookingCtx();
  const employee = useEmployeeById(selected?.employeeId);

  const mutation = api.serviceEvents.createServiceEvent.useMutation(
    {},
    {
      onSuccess: () => {
        toast.message(
          <div className='flex flex-row items-center gap-3 select-none'>
            <img
              src='/assets/note.png'
              alt='Appointment booked'
              width={64}
              height={64}
              className='size-16 cursor-default rounded-[24px] [-webkit-user-drag:none] [user-drag:none]'
            />
            <div className='text-sm'>
              Booked <span className='text-muted-foreground'>appointment</span>{' '}
              <span className='whitespace-nowrap'>
                <span className='text-muted-foreground'>on</span>{' '}
                <DateTimeInUTC value={params.selectedDate} format='EEEE, MMM d, yyyy' />
              </span>{' '}
              <span className='whitespace-nowrap'>
                <span className='text-muted-foreground'>with</span> {employee?.first_name} {employee?.last_name}
              </span>
            </div>
          </div>,
          {
            style: {
              borderRadius: '24px',
            },
          }
        );
        refetch();
        clearBooking();
      },
      meta: {
        toastError: 'Failed to book appointment',
      },
    }
  );

  const reschedule = api.serviceEvents.rescheduleServiceEvent.useMutation(
    {
      path: {
        service_event_id: editingServiceEvent?.id || '',
      },
    },
    {
      onMutate: () => {
        if (!editingServiceEvent) {
          throw new Error('No appointment to reschedule');
        }
      },
      onSuccess: () => {
        toast.success('Appointment rescheduled');
        refetch();
        clearBooking();
      },
      meta: {
        toastError: 'Failed to reschedule appointment',
      },
    }
  );

  const onBookAppointment = useCallback(() => {
    if (!selected) {
      return null;
    }

    mutation.mutate({
      office_id: customer.office_id,
      customer_id: customer.id,
      service_type_id: params.serviceTypeId,
      service_plan_id: params.servicePlanId,
      service_type_template_id: params.serviceTypeTemplateId,
      route_id: selected.routeId,
      status: 'PENDING',
      scheduling_type: 'FIXED',
      date: format(params.selectedDate, 'yyyy-MM-dd'),
      start_time: selected.slot.start_time,
      end_time: selected.slot.end_time,
      window_start_time: selected.windowStartTime,
      window_end_time: selected.windowEndTime,
      duration_minutes: params.durationMinutes,
      notes: note,
    });
  }, [mutation, customer, selected, params, note]);

  const onReschedule = useCallback(() => {
    if (!selected || !editingServiceEvent) return;
    reschedule.mutate({
      service_event_id: editingServiceEvent.id,
      route_id: selected.routeId,
      new_date: format(params.selectedDate, 'yyyy-MM-dd'),
      new_start_time: selected.slot.start_time,
      new_end_time: selected.slot.end_time,
      new_window_start_time: selected.windowStartTime,
      new_window_end_time: selected.windowEndTime,
      new_duration_minutes: params.durationMinutes,
      pending_confirmation: true,
      notes: note,
    });
  }, [reschedule, selected, params, editingServiceEvent, note]);

  if (!selected) {
    return null;
  }

  return (
    <div>
      <div className='sticky top-0 z-10 flex w-full items-center gap-2 border-b bg-background px-4 py-2'>
        <Button
          size='icon'
          variant='ghost'
          onClick={() => {
            setSelected(null);
          }}
        >
          <ChevronLeftIcon className='size-4' />
        </Button>
        <h2 className='text-heading-sm'>{editingServiceEvent ? 'Reschedule Appointment' : 'Confirm Appointment'}</h2>
      </div>
      <div className='flex flex-col gap-6 p-4'>
        <h2 className='text-heading-md font-medium'>
          {params.serviceName} <span className='text-muted-foreground'>on</span>{' '}
          <DateTimeInUTC value={params.selectedDate} format='EEEE, MMM d, yyyy' />{' '}
          <span className='text-muted-foreground'>with</span> {employee?.first_name} {employee?.last_name}
        </h2>
        <div className='space-y-4'>
          <div className='text-sm font-medium'>Appointment Details</div>
          <div className='flex flex-col'>
            <div className='inline-flex items-center border-t-[0.5px] py-2'>
              <div className='w-40 text-sm text-muted-foreground'>Service Type</div>
              <div className='text-sm'>{params.serviceName}</div>
            </div>
            <div className='inline-flex items-center border-t-[0.5px] py-2'>
              <div className='w-40 text-sm text-muted-foreground'>Duration</div>
              <div className='text-sm'>{params.durationMinutes} minutes</div>
            </div>
            <div className='inline-flex items-center border-t-[0.5px] py-2'>
              <div className='w-40 text-sm text-muted-foreground'>Time</div>
              <div className='text-sm'>
                <TimeInUTC value={selected?.windowStartTime} format='h:mm a' /> -{' '}
                <TimeInUTC value={selected?.windowEndTime} format='h:mm a' />
              </div>
            </div>
          </div>
        </div>
        {employee && (
          <div className='space-y-4'>
            <div className='text-sm font-medium'>Technician</div>
            <div className='flex w-full items-center gap-2 rounded-3xl p-3 shadow-md'>
              <EmployeeAvatar employeeId={selected?.employeeId} />
              <div className='flex flex-col gap-1.5'>
                <div className='text-sm leading-none'>
                  {employee.first_name} {employee.last_name}
                </div>
                <TravelDetails
                  className='text-xs leading-none text-muted-foreground'
                  travelDistanceMeters={selected.slot.travel_distance_meters}
                  travelTimeSeconds={selected.slot.travel_time_seconds}
                />
              </div>
            </div>
          </div>
        )}
      </div>
      <div className='flex flex-col gap-6 p-4'>
        <div className='flex flex-col gap-2'>
          <div className='text-sm font-medium'>Add a note</div>
          <Textarea
            placeholder='Add an appointment note...'
            className='min-h-24'
            value={note}
            onChange={(e) => setNote(e.target.value)}
          />
        </div>
        {editingServiceEvent ? (
          <Button
            size='sm'
            className='w-fit'
            onClick={onReschedule}
            disabled={!editingServiceEvent || reschedule.isPending}
          >
            Reschedule appointment
          </Button>
        ) : (
          <Button size='sm' className='w-fit' onClick={onBookAppointment} disabled={mutation.isPending}>
            Book appointment
          </Button>
        )}
      </div>
    </div>
  );
}

interface TravelDetailsProps {
  className?: string;
  travelDistanceMeters: number | null | undefined;
  travelTimeSeconds: number | null | undefined;
}

function TravelDetails({ className, travelDistanceMeters, travelTimeSeconds }: TravelDetailsProps) {
  if (!isFiniteNumber(travelDistanceMeters) && !isFiniteNumber(travelTimeSeconds)) {
    return null;
  }

  return (
    <div className={cn('inline-flex gap-2', className)}>
      {isFiniteNumber(travelDistanceMeters) && (
        <span>+{formatMetersAsMiles(Math.max(0, travelDistanceMeters))} miles</span>
      )}
      {isFiniteNumber(travelTimeSeconds) && <span>+{formatTravelTime(Math.max(0, travelTimeSeconds))}</span>}
    </div>
  );
}

function isFiniteNumber(value: number | null | undefined): value is number {
  return value !== null && value !== undefined && Number.isFinite(value);
}
